import { useState, type ReactElement } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Alert, Badge, Button, ProgressBar, Spinner } from '@csa/ui';
import { api, ApiError } from '../lib/api.js';
import type { JobStreamState } from '../lib/useJobStream.js';

/**
 * What happened to a submission.
 *
 * The three outcomes -- needs approval, running, finished -- look very
 * different to a user, and collapsing them into one status line is how
 * people end up waiting on a job that is actually sitting in someone
 * else's approval queue.
 */

export interface SubmitResponse {
  readonly jobId: string;
  readonly status: string;
  readonly payloadHash: string;
  readonly requiresApproval: boolean;
  readonly requiredApprovals: number;
  readonly approvalRequestId: string | null;
  readonly reason: string;
  readonly statementClass: string | null;
  readonly credentialKind: 'read' | 'write';
  readonly deduplicated: boolean;
  readonly warning: string | null;
}

/**
 * The outcome in one line, for the header.
 *
 * The full panel is worth its vertical space while something needs doing
 * -- an approval to chase, an error to read, a file to download -- and
 * costs it for nothing once a query has simply finished, which is most of
 * the time. So the state always shows here, and the detail is a click
 * away.
 */
export function SubmitStatusChip({
  outcome,
  stream,
  expanded,
  onToggle,
  controls,
}: {
  outcome: SubmitResponse;
  stream: JobStreamState;
  expanded: boolean;
  onToggle: () => void;
  controls: string;
}): ReactElement {
  const { tone, label } = chipState(outcome, stream);

  return (
    <>
      {/*
        The live region is a sibling, not the button itself. A control
        cannot also be a status region -- `aria-expanded` is not supported
        on `role="status"` -- and the announcement is what matters here:
        someone who has looked away needs to be told the job finished.
      */}
      <span className="sr-only" role="status" aria-live="polite">
        {label}
      </span>

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={controls}
        className={clsx(
          'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
          tone === 'danger' && 'border-danger-border bg-danger-bg text-danger-fg',
          tone === 'warning' && 'border-warning-border bg-warning-bg text-warning-fg',
          tone === 'success' && 'border-success-border bg-success-bg text-success-fg',
          tone === 'info' && 'border-info-border bg-info-bg text-info-fg',
          tone === 'neutral' && 'border-line bg-raised text-body',
        )}
      >
        {stream.status === 'running' || stream.status === 'queued' ? (
          <Spinner className="size-3.5" label={null} />
        ) : null}
        <span>{label}</span>
        {/* The affordance has to be visible, or a status that happens to be
            clickable is a status nobody clicks. */}
        <ChevronDownIcon
          className={clsx('size-4 opacity-70 transition-transform', expanded && 'rotate-180')}
          aria-hidden
        />
      </button>
    </>
  );
}

function chipState(
  outcome: SubmitResponse,
  stream: JobStreamState,
): { tone: 'danger' | 'warning' | 'success' | 'info' | 'neutral'; label: string } {
  if (outcome.requiresApproval) return { tone: 'info', label: 'Waiting for approval' };

  switch (stream.status) {
    case 'failed':
      return { tone: 'danger', label: `Failed — ${stream.error?.code ?? 'see detail'}` };
    case 'rejected':
      return { tone: 'warning', label: 'Rejected' };
    case 'expired':
      return { tone: 'warning', label: 'Expired' };
    case 'cancelled':
      return { tone: 'warning', label: 'Cancelled' };
    case 'succeeded':
      return {
        tone: 'success',
        label:
          stream.rowsRead !== null
            ? `Finished — ${stream.rowsRead.toLocaleString()} row${stream.rowsRead === 1 ? '' : 's'}`
            : 'Finished',
      };
    case 'queued':
      return { tone: 'neutral', label: 'Queued' };
    default:
      /**
       * An error on a job that is not finished is the previous attempt's,
       * and it is the most useful thing to say about a job that looks
       * stuck -- which is exactly when someone reads this.
       */
      return {
        tone: stream.error ? 'warning' : 'neutral',
        label: stream.error ? 'Running — last attempt failed' : 'Running…',
      };
  }
}

export function SubmitOutcome({
  outcome,
  stream,
}: {
  outcome: SubmitResponse;
  stream: JobStreamState;
}): ReactElement {
  if (outcome.requiresApproval) return <AwaitingApproval outcome={outcome} />;

  if (stream.status === 'failed') {
    return (
      <Alert tone="danger" title="The job failed">
        {stream.error?.message ?? 'No further detail was recorded.'}
        {stream.error?.code ? (
          <span className="ml-2 font-mono text-xs opacity-70">{stream.error.code}</span>
        ) : null}
      </Alert>
    );
  }

  if (stream.status === 'rejected' || stream.status === 'expired') {
    return (
      <Alert tone="warning" title={stream.status === 'rejected' ? 'Rejected' : 'Expired'}>
        {stream.status === 'rejected'
          ? 'An approver declined this request. Nothing was executed.'
          : 'This request was not approved in time and has been closed. Nothing was executed.'}
      </Alert>
    );
  }

  if (stream.status === 'succeeded') return <Succeeded outcome={outcome} stream={stream} />;

  return <Running outcome={outcome} stream={stream} />;
}

function AwaitingApproval({ outcome }: { outcome: SubmitResponse }): ReactElement {
  return (
    <Alert
      tone="info"
      title="Waiting for approval"
      icon={<ClockIcon className="size-5" aria-hidden />}
    >
      <p>
        {outcome.reason}. This needs {outcome.requiredApprovals} approver
        {outcome.requiredApprovals === 1 ? '' : 's'}, and you cannot approve your own request.
      </p>

      {/* The hash is shown because it is what an approver's decision binds
          to. Editing and resubmitting produces a different hash and a
          different request, and seeing it makes that rule legible. */}
      <p className="mt-2 text-xs opacity-80">
        Payload hash <code className="font-mono">{outcome.payloadHash.slice(0, 16)}…</code> · Job{' '}
        <code className="font-mono">{outcome.jobId}</code>
      </p>

      {outcome.warning ? <p className="mt-2 font-medium">{outcome.warning}</p> : null}
    </Alert>
  );
}

function Running({
  outcome,
  stream,
}: {
  outcome: SubmitResponse;
  stream: JobStreamState;
}): ReactElement {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-raised px-3 py-2 text-sm text-body"
      role="status"
      aria-live="polite"
    >
      <Spinner className="size-4 text-brand" label={null} />
      <span>{stream.phase === 'streaming' ? 'Exporting' : 'Running'}…</span>

      {stream.rowsRead !== null ? (
        <span className="text-muted">{stream.rowsRead.toLocaleString()} rows so far</span>
      ) : null}

      {/* Which account it is running as. A user who believes they are on
          the read account and sees "write" here has caught something
          before it matters. */}
      <Badge tone={outcome.credentialKind === 'write' ? 'warning' : 'neutral'}>
        {outcome.credentialKind} account
      </Badge>

      {/* Indeterminate on purpose: the row count of a running query is not
          known until it finishes, and a bar that guesses is a bar that
          lies. */}
      <ProgressBar value={null} label="Job progress" className="ml-auto w-32" />

      {/* The error from the last attempt, on a job that has not finished.
          A job being retried looks identical to one that is simply slow,
          and this is the only thing that distinguishes them. */}
      {stream.error ? (
        <p className="w-full border-t border-line pt-2 text-xs text-warning-fg">
          The last attempt failed and it is being retried: {stream.error.message}
          <span className="ml-2 font-mono opacity-70">{stream.error.code}</span>
        </p>
      ) : null}
    </div>
  );
}

function Succeeded({
  outcome,
  stream,
}: {
  outcome: SubmitResponse;
  stream: JobStreamState;
}): ReactElement {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The URL is fetched on click and used immediately.
   *
   * Never held in state, never rendered as an href. A presigned URL is a
   * bearer credential: anything that puts it in the DOM puts it in a
   * screenshot, a browser history entry, and the reach of every installed
   * extension.
   */
  const download = async (): Promise<void> => {
    setDownloading(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>(`/api/jobs/${outcome.jobId}/download`);
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not get a download link');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Alert tone="success" title="Finished">
      <div className="flex flex-wrap items-center gap-3">
        <span>
          {stream.rowsRead !== null
            ? `${stream.rowsRead.toLocaleString()} rows.`
            : 'The job completed.'}
        </span>

        {stream.hasResult ? (
          <Button
            size="sm"
            variant="secondary"
            leadingIcon={<ArrowDownTrayIcon className="size-4" aria-hidden />}
            onClick={() => void download()}
            disabled={downloading}
          >
            {downloading ? 'Preparing…' : 'Download'}
          </Button>
        ) : null}
      </div>

      {stream.hasResult ? (
        <p className="mt-1 text-xs opacity-80">
          The link is valid for a few minutes and its issue is recorded in the audit trail.
        </p>
      ) : null}

      {error ? <p className="mt-2 font-medium">{error}</p> : null}
    </Alert>
  );
}
